import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
  Inject,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { IExternalData, LGEDataService } from '../../../commons';
import * as fromExternalManagement from '../../../screens/external-users-management/state/external-users-management.selectors';
import * as ExternalManagementActions from '../../../screens/external-users-management/state/external-users-management.actions';
import { Store } from '@ngrx/store';
import { AlertModalComponent } from '../alert/alert.component';
import { LoaderModalComponent } from '../loader/loader.component';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { ConfirmModalComponent } from '..';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'external-access-details-modal',
  templateUrl: './external-access-details.component.html',
  styleUrls: ['./external-access-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalAccessRequestComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() externalData: IExternalData;
  @Input() isApproving: boolean;

  extOrgInfoFormGroup: FormGroup;
  extOrgContactInfoFormGroup: FormGroup;
  extUserFormGroup: FormGroup;
  confirm: any;
  alert: any;
  loader: any;

  constructor(
    private store: Store<fromExternalManagement.State>,
    private _dataService: LGEDataService,
    private ref: ChangeDetectorRef,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ExternalAccessRequestComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) private data: any
  ) {
    this.title = data.title;
    this.externalData = data.externalData;
    this.isApproving = data.isApproving;
  }

  ngOnInit() {
    this.createForms();
  }

  private createForms(): void {
    //Org Info
    this.extOrgInfoFormGroup = this.fb.group({
      orgInfoExtId: [null],
      name: [
        this.externalData.orgInfoExt.name,
        [Validators.required, Validators.maxLength(50)],
      ],
      EIN: [
        this.externalData.orgInfoExt.EIN,
        [
          Validators.required,
          Validators.maxLength(10),
          Validators.pattern(new RegExp(/^\d{2}-\d{7}/)),
        ],
      ],
      address1: [
        this.externalData.orgInfoExt.address1,
        [Validators.required, Validators.maxLength(35)],
      ],
      address2: [
        this.externalData.orgInfoExt.address2,
        [Validators.maxLength(35)],
      ],
      city: [
        this.externalData.orgInfoExt.city,
        [Validators.required, Validators.maxLength(50)],
      ],
      state: [this.externalData.orgInfoExt.state, Validators.required],
      zipCode: [
        this.externalData.orgInfoExt.zipCode,
        [
          Validators.required,
          Validators.pattern(new RegExp(/^\d{5}$|^\d{9}$/)),
        ],
      ],
      webAddress: [this.externalData.orgInfoExt.webAddress],
      subsection_501: [this.externalData.orgInfoExt.subsection_501],
    });
    //Contact Info
    this.extOrgContactInfoFormGroup = this.fb.group({
      orgContactInfoExtId: [null],
      title: [this.externalData.orgContactInfoExt.title],
      firstName: [
        this.externalData.orgContactInfoExt.firstName,
        Validators.required,
      ],
      lastName: [
        this.externalData.orgContactInfoExt.lastName,
        Validators.required,
      ],
      phone: [
        this.externalData.orgContactInfoExt.phone,
        [
          Validators.required,
          Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/)),
        ],
      ],
      email: [
        this.externalData.orgContactInfoExt.email,
        [
          Validators.required,
          Validators.maxLength(254),
          Validators.pattern(
            new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
          ),
        ],
      ],

      secondaryOrgContactInfoExtId: [null],
      secondaryTitle: [''],
      secondaryFirstName: [''],
      secondaryLastName: [''],
      secondaryPhone: [
        '',
        [Validators.pattern(new RegExp(/^\d{3}-\d{3}-\d{4}/))],
      ],
      secondaryEmail: [
        '',
        [
          Validators.maxLength(254),
          Validators.pattern(
            new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
          ),
        ],
      ],
    });
    //User Info
    this.extUserFormGroup = this.fb.group({
      externalUserId: [null],
      orgInfoExtId: [null],
      orgContactInfoExtId: [null],
      firstName: [
        this.externalData.externalUser.firstName,
        Validators.required,
      ],
      lastName: [this.externalData.externalUser.lastName, Validators.required],
      email: [
        this.externalData.externalUser.email,
        [
          Validators.required,
          Validators.maxLength(254),
          Validators.pattern(
            new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
          ),
        ],
      ],
      accessStatus: [this.externalData.externalUser.accessStatus],
      isVerified: [this.externalData.externalUser.isVerified],
    });
  }

  getTaxExemptStatus(value) {
    if (value === '') return 'Other';
    else if (isNaN(value) || +value === 0) {
      return value;
    } else {
      return `501(c)(${+value})`;
    }
  }

  onClose() {
    if (
      ![1, 2].includes(this.externalData.externalUser.isVerified) &&
      ['APPROVED', 'COMPLETED'].includes(
        this.externalData.externalUser.accessStatus.toUpperCase()
      )
    ) {
      this.store.dispatch(
        new ExternalManagementActions.CharityCheck(this.externalData)
      );
    }
    this.dialogRef.close();
  }

  onReject() {
    let rejectedUser: IExternalData = {
      orgInfoExt: this.externalData.orgInfoExt,
      orgContactInfoExt: this.externalData.orgContactInfoExt,
      externalUser: Object.assign({}, this.externalData.externalUser, {
        accessStatus: 'rejected',
      }),
    };
    this.store.dispatch(new ExternalManagementActions.RejectUser(rejectedUser));
  }

  onApprove() {
    this.store.dispatch(
        new ExternalManagementActions.AddExceptionEIN(this.externalData, true)
    );
    // try {
    //   let response = await this.addEinException();
    //   if (response) {
    //     this.dialog.closeAll();
    //     this.approvingUser();
    //   }
    // } catch (error) {
    //   this.dialog.closeAll();
    //   this.showAlert(
    //     'Error',
    //     `Unable to save this Orginization Info of Unverified EIN to Exception list. Error log: ${error} || ${error?.message}`
    //   );
    // }
    // this._dataService.EINOrgInfoDetailIUD(einOrgInfo).subscribe({
    //     next: () => {
    //         this.dialog.closeAll();
    //         this.approvingUser();
    //     },
    //     error: error => {
    //         this.dialog.closeAll();
    //         this.showAlert('Error', `Unable to save this Orginization Info of Unverified EIN to Exception list. Error log: ${error.message}`);
    //     }
    // })
  }

  approvingUser() {
    let approvedUser: IExternalData = {
      orgInfoExt: this.externalData.orgInfoExt,
      orgContactInfoExt: this.externalData.orgContactInfoExt,
      externalUser: Object.assign({}, this.externalData.externalUser, {
        accessStatus:
          this.externalData.externalUser.accessStatus === 'pendingEIN'
            ? 'approvedEIN'
            : 'approved',
        isVerified: 1,
      }),
    };
    this.store.dispatch(
      new ExternalManagementActions.ApproveUser(approvedUser)
    );
  }
  public showConfirm(title: string, message: string) {
    this.confirm = this.dialog.open(ConfirmModalComponent, {
      width: '350px',
      disableClose: false,
      data: { title: title, message: message },
    });
  }

  public showAlert(title: string, message: string) {
    this.alert = this.dialog.open(AlertModalComponent, {
      width: '260px',
      disableClose: true,
      data: { title: title, message: message },
    });
  }
  public showLoader(message: string) {
    this.loader = this.dialog.open(LoaderModalComponent, {
      width: '200px',
      disableClose: true,
      data: { message: message },
    });
  }

  ngOnDestroy() {}
}
